import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { appointmentsApi } from "../../shared/api/appointments";
import { apiError } from "../../shared/api/client";
import { schedulesApi, schedulesQueryKeys } from "../../shared/api/schedules";
import { PageHeader } from "../../shared/ui/PageHeader";
import { isoDate } from "../../shared/format";
import { WorkShiftsSettingsPage } from "./WorkShiftsSettingsPage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Tab = "duty" | "leaves";

const TABS: { key: Tab; label: string }[] = [
  { key: "duty", label: "Lịch trực" },
  { key: "leaves", label: "Nghỉ phép" },
];

/**
 * #19937 — Lịch làm việc (bác sĩ): MỘT lịch làm việc duy nhất, ở nhóm Nhân sự & Quyền.
 *
 * <ul>
 *   <li><b>Lịch trực</b> — các ca theo Khoa / Phòng / Bác sĩ, lặp theo thứ hoặc theo ngày cụ thể
 *       (trước là trang Phân ca ở Cấu hình lịch đặt online). Mọi kênh đặt lịch đọc cùng lịch này.
 *   <li><b>Nghỉ phép</b> — ngày nghỉ của từng bác sĩ; bác sĩ nghỉ ngày nào thì không kênh nào đặt
 *       được bác sĩ đó vào ngày đó.
 * </ul>
 *
 * Tab đang mở nằm trên URL ({@code ?tab=leaves}) để đường dẫn chia sẻ được.
 */
export function SchedulesSettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "leaves" ? "leaves" : "duty";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lịch làm việc (bác sĩ)"
        description="Lịch trực của bác sĩ theo khoa, phòng, thứ hoặc ngày cụ thể, và ngày nghỉ phép. Mọi kênh đặt lịch (lễ tân, website, Mini App, AI) dùng chung lịch này."
      />

      <div role="tablist" aria-label="Lịch làm việc" className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setParams(t.key === "duty" ? {} : { tab: t.key }, { replace: true })}
            className={
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium " +
              (tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "duty" ? <WorkShiftsSettingsPage embedded /> : <LeavesTab />}
    </div>
  );
}

function LeavesTab() {
  const qc = useQueryClient();
  const [doctorId, setDoctorId] = useState<string>("");

  const doctors = useQuery({
    queryKey: ["doctor-options"],
    queryFn: () => appointmentsApi.listDoctors(),
  });

  // auto-select first doctor on load
  if (!doctorId && doctors.data && doctors.data.length > 0) {
    setDoctorId(doctors.data[0].id);
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="grid max-w-sm gap-1.5">
            <Label htmlFor="doctor-picker">Bác sĩ</Label>
            <Select value={doctorId || undefined} onValueChange={(v) => setDoctorId(v)}>
              <SelectTrigger id="doctor-picker">
                <SelectValue placeholder="— Chọn bác sĩ —" />
              </SelectTrigger>
              <SelectContent>
                {(doctors.data ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                    {d.specialty ? ` — ${d.specialty}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {doctors.isError && (
              <p className="text-sm text-destructive">Không tải được danh sách bác sĩ.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {doctorId && <LeaveEditor doctorId={doctorId} qc={qc} />}
    </div>
  );
}

// =============================================================== Leave editor

function LeaveEditor({
  doctorId,
  qc,
}: {
  doctorId: string;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const query = useQuery({
    queryKey: schedulesQueryKeys.leaves(doctorId),
    queryFn: () => schedulesApi.listLeaves(doctorId),
  });

  // Ngày theo giờ địa phương (#19847).
  const [date, setDate] = useState<string>(isoDate(new Date()));
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    qc.invalidateQueries({ queryKey: schedulesQueryKeys.leaves(doctorId) });
  }

  const add = useMutation({
    mutationFn: () => schedulesApi.addLeave(doctorId, date, reason),
    onSuccess: () => {
      setReason("");
      setError(null);
      refresh();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const remove = useMutation({
    mutationFn: (leaveId: string) => schedulesApi.removeLeave(doctorId, leaveId),
    onSuccess: refresh,
  });

  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="mb-3 text-base font-semibold text-foreground">
          Lịch nghỉ phép
        </h2>

        <div className="mb-3 grid gap-2 sm:grid-cols-[180px_1fr_auto]">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Input
            type="text"
            placeholder="Lý do (không bắt buộc) — VD: Đi đào tạo"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button
            type="button"
            onClick={() => {
              setError(null);
              add.mutate();
            }}
            disabled={add.isPending || !date}
          >
            + Thêm ngày nghỉ
          </Button>
        </div>

        {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

        {query.isLoading && (
          <p className="text-sm text-muted-foreground">Đang tải…</p>
        )}

        {!query.isLoading && (query.data?.length ?? 0) === 0 && (
          <p className="text-sm text-muted-foreground">
            Không có ngày nghỉ nào trong khoảng thời gian gần đây.
          </p>
        )}

        {(query.data?.length ?? 0) > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Ngày nghỉ</TableHead>
                <TableHead>Lý do</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(query.data ?? []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.leaveDate}</TableCell>
                  <TableCell>
                    {l.reason ?? <em className="text-muted-foreground">—</em>}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Xoá ngày nghỉ"
                      onClick={() => remove.mutate(l.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
